import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import alert from "components/elements/Alert";

import { generateRandomString } from "utils/helper";
import * as services from "../services/policyApi";
import { setIsLoading, setPolicyCategory } from "../slices/policySlice";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";

function usePolicyList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const isLoading = useSelector((state) => state.policy.isLoading);
  const policyCategory = useSelector((state) => state.policy.policyCategory);
  const policyList = useSelector((state) => state.policy.policyList);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentPolicyCategory, setCurrentPolicyCategory] = useState({});
  const [category, setCategory] = useState({
    policyCategoryName: currentPolicyCategory?.policyCategoryName
  })

  const initialValues = useMemo(() => {
    return currentPolicyCategory;
  }, [currentPolicyCategory]);

  const getPolicyCategory = useCallback(async (values) => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getPolicyCategory({
        requestId: generateRandomString(),
        companyId: currentCompany,
        page: 0,
        size: 50,
        ...values,
      });
      const category = response?.data?.content[0]?.policyCategory || [];
      let bufferCategory = [...category];

      let idx = 0;
      for (let bufferCat of bufferCategory) {
        try {
          const res = await services.getPolicyByCategory({
            requestId: generateRandomString(),
            companyId: currentCompany,
            policyCategoryId: bufferCat.policyCategoryId,
            ...values
          });
          bufferCategory[idx] = {
            ...bufferCategory[idx],
            policyList: res.data?.policyList || [],
          };
        } catch (error) {
          message.error(t("common.error"))
        } finally {
          idx++;
        }
      }
      dispatch(setPolicyCategory(bufferCategory));
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch]);


  const onSearch = useCallback(async (values) => {
    values.page = 0;
    values.size = 50;
    getPolicyCategory(values)
    console.log("onSearch ", values);

  }, [currentCompany, dispatch]);

  const onCancel = useCallback(() => {
    setCurrentPolicyCategory({});
    setModalOpen(false);
  }, []);

  const onCreatePolicyCategory = useCallback(() => {
    setCurrentPolicyCategory({});
    setModalOpen(true);
  }, []);

  const onEditPolicyCategory = useCallback((record) => {
    setCurrentPolicyCategory(record);
    setModalOpen(true);
  }, []);


  const onDeletePolicy = useCallback(async (row, record) => {
    try {
      dispatch(setIsLoading(true));
      const modifiedValues = {
        policyCategoryId: row.policyCategoryId,
        statusFlag: record.announcementStatus,
        policyAnnouncementId: record.id,
        announcementStatus: record.announcementStatus,
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: currentEmployee
      };
      await services.deleteAnnouncementPolicy(modifiedValues);
      message.success(t("common.success"))
      onCancel();
      getPolicyCategory({ page: 0, size: 50 });
    } catch (error) {
      message.error(t("common.error"))
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, dispatch, onCancel, getPolicyCategory])

  const createPolicyCategory = useCallback(
    async (values = {}) => {
      try {
        const modifiedValues = {
          policyCategoryName: values.policyCategoryName,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        await services.createPolicyCategory(modifiedValues);
        message.success(t("common.success"))
        dispatch(setIsLoading(true));
        onCancel();
        getPolicyCategory({ page: 0, size: 50 });
        setCategory("");
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getPolicyCategory]
  );
  const modifiedPolicyCategory = useMemo(() => {
    const modifiedOptions = policyCategory?.map?.((opt) => ({
      ...opt,
      label: opt.policyCategoryName,
      value: opt.policyCategoryId,
    }));
    return modifiedOptions;
  }, [policyCategory]);

  const updatePolicyCategory = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          policyCategoryId: values.policyCategoryId,
          policyCategoryName: values.policyCategoryName,
          announcementStatus: values.policyCategoryStatus,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        await services.updatePolicyCategory(modifiedValues);
        message.success(t("common.success"))
        onCancel();
        getPolicyCategory({ page: 0, size: 50 });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getPolicyCategory]
  );

  const managePolicyCategoryStatus = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          policyCategoryId: values.policyCategoryId,
          statusFlag: values.announcementStatus,
          announcementStatus: values.announcementStatus,
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: currentEmployee
        };
        await services.managePolicyCategoryStatus(modifiedValues);
        message.success(t("common.success"))
        onCancel();
        getPolicyCategory({ page: 0, size: 50 });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, onCancel, getPolicyCategory]
  );

  const updateAnnouncementPolicy = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          policyName: values.policyName,
          policyCategoryId: values.policyCategoryId,
          policyAnnouncementId: values.policyAnnouncementId,
          announcementStatus: values.announcementStatus,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        await services.updateAnnouncementPolicy(modifiedValues);
        message.success(t("common.success"))
        getPolicyCategory({ page: 0, size: 50 });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, getPolicyCategory]
  );

  const manageAnnouncementPolicyStatus = useCallback(
    async (values = {}) => {
      try {
        dispatch(setIsLoading(true));
        const modifiedValues = {
          policyCategoryId: values.policyCategoryId,
          policyAnnouncementId: values.id,
          statusFlag: values.announcementStatus,
          announcementStatus: values.announcementStatus,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        await services.manageAnnouncementPolicyStatus(modifiedValues);
        message.success(t("common.success"))
        getPolicyCategory({ page: 0, size: 50 });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, getPolicyCategory]
  );

  const onSubmit = useCallback(
    (values) => {
      if (values.policyCategoryId) {
        updatePolicyCategory(values);
      } else {
        createPolicyCategory(values);
      }
      // alert({ message: "Success" });
    },
    [createPolicyCategory, updatePolicyCategory]
  );

  const onCreate = useCallback(() => {
    navigate("/news_policy/policy/create");
  }, [navigate]);

  const onEdit = useCallback(
    (row, record) => {
      if (row.policyCategoryId && record.id) {
        navigate(
          `/news_policy/policy/${record.id}/edit?policyCategoryId=${row.policyCategoryId}`
        );
      }
    },
    [navigate]
  );

  const onStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะหรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            if (record.policyCategoryId) {
              managePolicyCategoryStatus({
                ...record,
                announcementStatus: val ? "Y" : "N",
              });

            }

            if (record.id) {
              manageAnnouncementPolicyStatus({
                ...record,
                announcementStatus: val ? "Y" : "N",
              });
            }
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [dispatch, updatePolicyCategory]
  );

  const onPolicyStatus = useCallback(
    async (val, record) => {
      try {
        dispatch(setIsLoading(true));
        if (record?.id) {
          manageAnnouncementPolicyStatus({
            ...record,
            announcementStatus: val ? "Y" : "N",
          });
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, updateAnnouncementPolicy]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getPolicyCategory({ page: 0, size: 50 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  return {
    isLoading,
    initialValues,
    isModalOpen,
    policyCategory,
    policyList,
    category,
    modifiedPolicyCategory,
    onSearch,
    onCreate,
    onEdit,
    onSubmit,
    onStatus,
    onDeletePolicy,
    onCancel,
    onCreatePolicyCategory,
    onEditPolicyCategory,
    onPolicyStatus
  };
}

export default usePolicyList;
